import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { IPedido } from '../interface/IPedido';
import { Environment } from '../../core/config/environment';
import { useAutenticacaoContext } from '../../core/contexts/AutenticacaoContext';
import { useSnackbar } from '../../core/contexts/SnackbarContext';
import { QrCode2, ReceiptLong } from '@mui/icons-material';
import { Link } from '@mui/material';

type WebSocketPedidosContextType = {
  ultimoPedidoAtualizado?: IPedido;
  registrarCallbackAtualizacao?: (callback: () => void) => void;
  removerCallbackAtualizacao?: (callback: () => void) => void;
};

const WebSocketPedidosContext = createContext<WebSocketPedidosContextType>({});

export const useWebSocketPedidosContext = () => useContext(WebSocketPedidosContext);

export const WebSocketPedidosProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ultimoPedidoAtualizado, setUltimoPedidoAtualizado] = useState<IPedido>();
  const { getToken, estabelecimento } = useAutenticacaoContext();
  const notificar = useSnackbar();
  const socketRef = useRef<Socket>();
  const callbacksRef = useRef<Set<() => void>>(new Set());

  useEffect(() => {
    const token = getToken("TOKEN_ESTABELECIMENTO_KEY");
    if (!token || !estabelecimento) return;

    const socket = io(Environment.MS_PEDIDOS_SOCKET, {
      auth: { token },
      reconnectionAttempts: 10, // padrão é infinito
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ WebSocket conectado');
    });

    socket.on('disconnect', (reason) => {
      console.warn('⚠️ WebSocket desconectado:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Erro ao conectar WebSocket:', error);
    });

    socket.on('pedido_atualizado', ({ pedido, tocarSino }: { pedido: IPedido; tocarSino: boolean }) => {
      console.log("📨 Pedido atualizado recebido:", pedido.uuid);
      setUltimoPedidoAtualizado(pedido);
      
      // Executar todos os callbacks registrados para atualizar telas
      callbacksRef.current.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('Erro ao executar callback de atualização:', error);
        }
      });
      
      if (tocarSino) {
        tocarSomDeSino(pedido);
      }
    });

    // QR Code por mesa: envios que aguardam o salão e pedidos de conta tocam o sino;
    // qualquer mudança de sessão (alguém entrou, conta reaberta) só atualiza as telas.
    socket.on('mesa:rodada-criada', ({ pedidoUuid, mesaId, aguardandoConfirmacao }: { pedidoUuid: string; mesaId: number; aguardandoConfirmacao: boolean }) => {
      executarCallbacks();
      avisarMesa(
        <>Mesa {mesaId}: {aguardandoConfirmacao ? 'novo pedido para confirmar' : 'novo pedido enviado à cozinha'} <Link color={"#fff"} href={`/pedidos/operacao/edicao/${pedidoUuid}?redirect=/pedidos/operacao?tipo=mesa`}>abrir</Link></>,
        aguardandoConfirmacao
      );
    });

    socket.on('mesa:conta-solicitada', ({ pedidoUuid, mesaId, solicitadaPor }: { pedidoUuid: string; mesaId: number; solicitadaPor?: string }) => {
      executarCallbacks();
      avisarMesa(
        <>Mesa {mesaId} pediu a conta{solicitadaPor ? ` (${solicitadaPor})` : ''} <Link color={"#fff"} href={`/pedidos/operacao/edicao/${pedidoUuid}?redirect=/pedidos/operacao?tipo=mesa`}>abrir</Link></>,
        true
      );
    });

    socket.on('mesa:sessao-atualizada', () => executarCallbacks());

    return () => {
      socket.disconnect();
    };
  }, [estabelecimento]);

  const tocarSomDeSino = (pedido: IPedido) => {
    const audio = new Audio('/audio/bell.wav');
    audio.play().catch((err) => {
      console.warn('Erro ao tocar som:', err);
    });
    notificar(<>Novo pedido recebido! <Link color={"#fff"} href={`/pedidos/operacao/edicao/${pedido.uuid}`}>{pedido.numeroPedido}</Link></>, {
      background: 'primary',
      icon: <ReceiptLong />
    });
  };

  const executarCallbacks = () => {
    callbacksRef.current.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Erro ao executar callback de atualização:', error);
      }
    });
  };

  const avisarMesa = (mensagem: React.ReactNode, tocarSom: boolean) => {
    if (tocarSom) {
      new Audio('/audio/bell.wav').play().catch((err) => console.warn('Erro ao tocar som:', err));
    }
    notificar(mensagem, { background: 'primary', icon: <QrCode2 /> });
  };

  const registrarCallbackAtualizacao = (callback: () => void) => {
    callbacksRef.current.add(callback);
  };

  const removerCallbackAtualizacao = (callback: () => void) => {
    callbacksRef.current.delete(callback);
  };

  return (
    <WebSocketPedidosContext.Provider value={{ 
      ultimoPedidoAtualizado,
      registrarCallbackAtualizacao,
      removerCallbackAtualizacao
    }}>
      {children}
    </WebSocketPedidosContext.Provider>
  );
};
